import { routes } from "@/routes/routes";
import type { ChatScenario, ChatStep, OnboardingFacts } from "../types/chat";
import { getChatStep } from "./steps";

// Full terms texts, linked from consents in any step.
const ALWAYS_ALLOWED: readonly string[] = [
  routes.terms.bc,
  routes.terms.anaf,
  routes.terms.partner,
];

const applies = (step: ChatStep, facts: OnboardingFacts) =>
  step.appliesWhen?.(facts) ?? true;

// Nothing left to do for this step right now.
const isSettled = (step: ChatStep, facts: OnboardingFacts) =>
  !applies(step, facts) || step.isDone(facts);

/**
 * The first step of the scenario that applies, is not done and has all its
 * dependencies settled. `null` only when the scenario ends without a last
 * step that always applies, which `validateScenario` rejects.
 */
export const selectNextStep = (
  scenario: ChatScenario,
  facts: OnboardingFacts,
): ChatStep | null =>
  scenario.steps
    .map(getChatStep)
    .find(
      (step) =>
        !isSettled(step, facts) &&
        step.needs.every((id) => isSettled(getChatStep(id), facts)),
    ) ?? null;

// Done steps that leave a summary bubble, in scenario order, up to the
// current step: a later step the backend already has (KYC kept from an
// earlier attempt) shows once the chat gets there, not before its question.
export const getDoneSteps = (
  scenario: ChatScenario,
  facts: OnboardingFacts,
): ChatStep[] => {
  const steps = scenario.steps.map(getChatStep);
  const current = selectNextStep(scenario, facts);
  const end = current ? steps.indexOf(current) : steps.length;

  return steps
    .slice(0, end)
    .filter(
      (step) => !!step.copy.done && applies(step, facts) && step.isDone(facts),
    );
};

const matchesPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isScreenAllowed = (step: ChatStep, pathname: string): boolean =>
  ALWAYS_ALLOWED.includes(pathname) ||
  step.screen.group.some((prefix) => matchesPrefix(pathname, prefix));
